import { useEffect, useState } from 'react';
import { Card, Col, Row, Statistic, Table, Alert, Spin, Tag, message } from 'antd';
import {
  TeamOutlined,
  FunnelPlotOutlined,
  CustomerServiceOutlined,
  BellOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import {
  getMobileDashboard,
  type MobileDashboardResponse,
  type MobileActivitySummary,
  type MobilePipelineStageSummary,
} from '@/api/mobileApi';
import MobileNav from './MobileNav';

export default function MobileDashboardPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canRead = hasPermission('mobile', 'read');

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MobileDashboardResponse | null>(null);

  useEffect(() => {
    if (!canRead) return;
    setLoading(true);
    getMobileDashboard()
      .then((res) => {
        if (res.data) setData(res.data);
      })
      .catch(() => message.error(t('app.error')))
      .finally(() => setLoading(false));
  }, [canRead]);

  const activityColumns: ColumnsType<MobileActivitySummary> = [
    {
      title: t('mobile.dashboard.activity_subject'),
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: t('mobile.dashboard.activity_type'),
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (v: string) => <Tag>{t(`mobile.dashboard.activity_types.${v}`, v)}</Tag>,
    },
    {
      title: t('mobile.dashboard.activity_status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: string) => (
        <Tag color={v === 'completed' ? 'green' : v === 'planned' ? 'blue' : 'default'}>
          {t(`mobile.dashboard.activity_statuses.${v}`, v)}
        </Tag>
      ),
    },
    {
      title: t('mobile.dashboard.activity_date'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v: string) => (v ? new Date(v).toLocaleString() : '-'),
    },
  ];

  const pipelineColumns: ColumnsType<MobilePipelineStageSummary> = [
    {
      title: t('mobile.dashboard.stage'),
      dataIndex: 'stage_id',
      key: 'stage_id',
      ellipsis: true,
    },
    {
      title: t('mobile.dashboard.count'),
      dataIndex: 'count',
      key: 'count',
      width: 80,
    },
    {
      title: t('mobile.dashboard.total_value'),
      dataIndex: 'total_value',
      key: 'total_value',
      width: 120,
      render: (v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 0 }),
    },
  ];

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;

  return (
    <div>
      <MobileNav />
      <h2>{t('mobile.dashboard_title')}</h2>
      <Spin spinning={loading}>
        {data && (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={12} sm={8} md={4}>
                <Card>
                  <Statistic
                    title={t('mobile.dashboard.total_customers')}
                    value={data.total_customers}
                    prefix={<TeamOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Card>
                  <Statistic
                    title={t('mobile.dashboard.total_opportunities')}
                    value={data.total_opportunities}
                    prefix={<FunnelPlotOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Card>
                  <Statistic
                    title={t('mobile.dashboard.open_tickets')}
                    value={data.open_tickets}
                    prefix={<CustomerServiceOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Card>
                  <Statistic
                    title={t('mobile.dashboard.unread_notifications')}
                    value={data.unread_notifications}
                    prefix={<BellOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Card>
                  <Statistic
                    title={t('mobile.dashboard.upcoming_activities')}
                    value={data.upcoming_activities}
                    prefix={<HistoryOutlined />}
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={14}>
                <Card title={t('mobile.dashboard.recent_activities')} size="small">
                  <Table
                    columns={activityColumns}
                    dataSource={data.recent_activities}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                </Card>
              </Col>
              <Col xs={24} md={10}>
                <Card title={t('mobile.dashboard.pipeline_summary')} size="small">
                  <Table
                    columns={pipelineColumns}
                    dataSource={data.pipeline_summary}
                    rowKey="stage_id"
                    pagination={false}
                    size="small"
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Spin>
    </div>
  );
}
